<!--
 * @Description: 客户签约主体列表页
 * @Author: mdb
 * @Date: 2023-02-17 11:27:35
 * @LastEditTime: 2023-03-14 14:00:25
 * @LastEditors: cxh
-->
<template>
  <dc-layout-main :is-breadcrumb="true">
    <dc-layout-content>
      <cos-table
        serial
        :columns="columns"
        v-model:search="queryForm"
        @fetch="getList"
        @mask="showMask"
        ref="tableRef"
        tableId="DcCosOFbuMainBody"
      >
        <template #search>
          <a-form :model="queryForm">
            <dc-form-item :label="t('mainBody.customerNumber')">
              <a-input
                v-model:value.trim="queryForm.customer.customerNumber"
                :maxlength="200"
                allowClear
                :placeholder="t('common.pleaseInput')"
              />
            </dc-form-item>
            <dc-form-item :label="t('mainBody.customerName')">
              <a-input
                v-model:value.trim="queryForm.customer.customerName"
                :maxlength="200"
                allowClear
                :placeholder="t('common.pleaseInput')"
              />
            </dc-form-item>
            <dc-form-item :label="t('mainBody.cosId')">
              <a-input
                v-model:value.trim="queryForm.customer.cosId"
                :maxlength="200"
                allowClear
                :placeholder="t('common.pleaseInput')"
              />
            </dc-form-item>
            <dc-form-item :label="t('mainBody.signBodyName')">
              <a-input
                v-model:value.trim="queryForm.signBodyName"
                :maxlength="200"
                allowClear
                :placeholder="t('common.pleaseInput')"
              />
            </dc-form-item>
            <dc-form-item :label="t('mainBody.customerCode')">
              <a-input
                v-model:value.trim="queryForm.customerCode"
                :maxlength="200"
                allowClear
                :placeholder="t('common.pleaseInput')"
              />
            </dc-form-item>
            <dc-form-item :label="t('mainBody.accountCode')">
              <a-input
                v-model:value.trim="queryForm.accountCode"
                :maxlength="200"
                allowClear
                :placeholder="t('common.pleaseInput')"
              />
            </dc-form-item>
            <dc-form-item :label="t('common.department')">
              <CustomerAreaCascader type="search" appId="cos_ofbu" v-model:ids="queryForm.customer.sellerRegionList" />
            </dc-form-item>
            <dc-form-item :label="t('mainBody.sellerName')">
              <UserSelect
                v-model:value="queryForm.customer.seller"
                :placeholder="t('common.pleaseSelect')"
                allowClear
              />
            </dc-form-item>
          </a-form>
        </template>

        <template #button>
          <ExportButton v-permission="'ofbu_main_body_btn'" appId="cos_ofbu" taskType="412" :search="queryForm">{{
            t('btn.export')
          }}</ExportButton>
        </template>

        <template #bodyCell="{ column, text }">
          <template v-if="column.dataIndex === 'signBodyBalanceList'">
            {{ text?.map((item: any) => `${item.currencyCode}：${item.quota}`).join('、') }}
          </template>
          <template v-if="column.key === 'sellerRegion'">
            <CustomerAreaCascaderText appId="cos_ofbu" :value="text" />
          </template>
        </template>
      </cos-table>
    </dc-layout-content>
  </dc-layout-main>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onBeforeMount } from 'vue'
import { useRoute } from 'vue-router'
import CustomerAreaCascader from '@/components/CustomerAreaCascader/index.vue'
import CustomerAreaCascaderText from '@/components/CustomerAreaCascader/CustomerAreaCascaderText.vue'
import { mainBodyList, mainBodyDetail } from '@/api/overseasSales/customerCenter'
import type { MainBodyParams } from '@/typings/api/customerCenter'
import { useI18n } from 'vue-i18n'
const { t } = useI18n()
// 主题状态枚举
const mainBodyStatusEnum = [
  { value: 0, label: t('mainBody.mainBodyStatus0') },
  { value: 1, label: t('mainBody.mainBodyStatus1') },
  { value: 2, label: t('mainBody.mainBodyStatus2') },
  { value: 3, label: t('mainBody.mainBodyStatus3') },
  { value: 4, label: t('mainBody.mainBodyStatus4') },
  { value: 5, label: t('mainBody.mainBodyStatus5') },
  { value: 6, label: t('mainBody.mainBodyStatus6') }
]

const tableRef = ref()
const queryForm = reactive<MainBodyParams>({ customer: {} })
const columns = computed(() => [
  { title: t('mainBody.customerNumber'), dataIndex: ['customer', 'customerNumber'] },
  { title: t('mainBody.customerName'), dataIndex: ['customer', 'customerName'] },
  { title: t('mainBody.cosId'), dataIndex: ['customer', 'cosId'] },
  { title: t('mainBody.customerCode'), dataIndex: 'customerCode' },
  { title: t('mainBody.accountCode'), dataIndex: 'accountCode', headerQuestion: t('mainBody.accountCodeDesc') },
  { title: t('mainBody.signBodyName'), dataIndex: 'signBodyName', headerQuestion: t('mainBody.signBodyNameDesc') },
  {
    title: t('mainBody.serviceBodyName'),
    dataIndex: 'serviceBodyName',
    headerQuestion: t('mainBody.serviceBodyNameDesc')
  },
  {
    title: t('mainBody.bodyContactName'),
    dataIndex: 'contactName',
    cosType: 'mask',
    headerQuestion: t('mainBody.contactNameDesc'),
    width: 150
  },
  {
    title: t('mainBody.bodyPhoneNumber'),
    dataIndex: 'phoneNumber',
    cosType: 'mask',
    headerQuestion: t('mainBody.phoneNumberDesc'),
    width: 145
  },
  {
    title: t('mainBody.signBodyBalanceList'),
    dataIndex: 'signBodyBalanceList',
    headerQuestion: t('mainBody.signBodyBalanceListDesc'),
    width: 200
  },
  { title: t('mainBody.totalQuota'), dataIndex: 'totalQuota', headerQuestion: t('mainBody.totalQuotaDesc') },
  { title: t('mainBody.allTotal'), dataIndex: 'allTotal', headerQuestion: t('mainBody.allTotalDesc'), minWidth: 130 },
  {
    title: t('mainBody.accountCycleTypeStr'),
    dataIndex: 'accountCycleTypeStr',
    headerQuestion: t('mainBody.accountCycleType')
  },
  {
    title: t('mainBody.reconciliationCycleTypeStr'),
    dataIndex: 'reconciliationCycleTypeStr',
    headerQuestion: t('mainBody.reconciliationCycleType')
  },
  {
    title: t('mainBody.signBusinessTypeStr'),
    dataIndex: 'signBusinessTypeStr',
    headerQuestion: t('mainBody.signBusiness')
  },
  {
    title: t('mainBody.contractedBusinessStr'),
    dataIndex: 'contractedBusinessStr',
    headerQuestion: t('mainBody.contractedBusinessDesc'),
    minWidth: 130
  },
  {
    title: t('mainBody.registerAreaCode'),
    dataIndex: 'registerAreaCode',
    headerQuestion: t('mainBody.registerAreaCodeDesc'),
    width: 160
  },
  {
    title: t('mainBody.signBodyType'),
    dataIndex: 'type',
    dicData: [
      { value: 'C101', label: t('mainBody.C101') },
      { value: 'C102', label: t('mainBody.C102') }
    ],
    headerQuestion: t('mainBody.typeDesc')
  },
  {
    title: t('mainBody.signBodyStatus'),
    dataIndex: 'signBodyStatus',
    headerQuestion: t('mainBody.signBodyStatusDesc'),
    dicData: mainBodyStatusEnum
  },
  { title: t('mainBody.examineTime'), dataIndex: 'examineTime', headerQuestion: t('mainBody.examineTimeDesc'), width: 190 },
  {
    title: t('mainBody.sellerName'),
    dataIndex: ['customer', 'sellerName'],
    headerQuestion: t('mainBody.sellerNameDesc'),
    minWidth: 130
  },
  { title: t('mainBody.sellerRegion'), dataIndex: ['customer', 'sellerRegion'], key: 'sellerRegion', minWidth: 190 }
])

// 检测 URL 是否带有查询参数，有则使用
const route = useRoute()
const queryCustomerCode = computed(() => route.query.customerCode as string)
onBeforeMount(() => {
  if (queryCustomerCode.value) queryForm.customerCode = queryCustomerCode.value
})

const getList = (queryForm: MainBodyParams, callBack: (res: any) => void) => {
  mainBodyList(queryForm).then((res) => {
    callBack(res)
  })
}

// 获取详情信息
const showMask = (record: any, callBack: (res: any) => void) => {
  mainBodyDetail({ id: record.id }).then((res) => {
    callBack(res.data)
  })
}
</script>
<script lang="ts">
export default { name: 'DcCosOFbuMainBody' }
</script>

<style lang="scss" scoped></style>
